import {
  ScatterChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Scatter,
  ReferenceLine,
  ReferenceArea,
  LabelList,
  Legend,
} from 'recharts'
import { ChartContainer, type ChartConfig } from './chart'
import {
  EmotionScatterChartPredictedDot,
  EmotionScatterChartLegendContent,
} from './EmotionScatterChartComponents'
import { BASE_EMOTIONS } from '@/constants/emotions'

interface EmotionScatterChartProps {
  valence: number
  arousal: number
  size?: 'medium' | 'large'
}

const EmotionScatterChart: React.FC<EmotionScatterChartProps> = ({
  valence,
  arousal,
  size = 'large',
}) => {
  const emotions = [
    ...BASE_EMOTIONS,
    { emotion: 'Predicted', x: valence, y: arousal },
  ]

  const chartConfig = {
    valence: {
      label: 'Valence',
      color: 'var(--chart-1)',
    },
    arousal: {
      label: 'Arousal',
      color: 'var(--chart-2)',
    },
    // Add colors for the quadrants and elements
    quadrant1: {
      label: 'High Arousal, Unpleasant',
      color: 'var(--chart-3)',
    },
    quadrant2: {
      label: 'High Arousal, Pleasant',
      color: 'var(--chart-4)',
    },
    quadrant3: {
      label: 'Low Arousal, Unpleasant',
      color: 'var(--chart-5)',
    },
    quadrant4: {
      label: 'Low Arousal, Pleasant',
      color: 'var(--chart-1)',
    },
    emotions: {
      label: 'Predefined Emotions',
      color: 'hsl(180, 70%, 50%)',
    },
    predicted: {
      label: 'Predicted Emotion',
      color: 'hsl(276, 70%, 60%)',
    },
  } satisfies ChartConfig

  return (
    <ChartContainer
      config={chartConfig}
      className={
        size === 'medium'
          ? 'h-[300px] w-full aspect-auto'
          : 'h-[400px] w-full aspect-auto'
      }
    >
      <ScatterChart margin={{ top: 0, right: 20, left: 0, bottom: 10 }}>
        <CartesianGrid />
        <XAxis
          type="number"
          dataKey="x"
          domain={[1, 100]}
          label={{
            value: 'Valence',
            position: 'insideBottom',
            offset: -10,
          }}
          ticks={[1, 25, 50, 75, 100]}
          tickFormatter={value => {
            if (value === 1) return 'Unpleasant'
            if (value === 100) return 'Pleasant'
            return value.toString()
          }}
        />
        <YAxis
          type="number"
          dataKey="y"
          domain={[1, 100]}
          ticks={[50, 100]}
          label={{
            value: 'Arousal',
            angle: -90,
            position: 'insideLeft',
          }}
        />
        {/* Middle Cross */}
        <ReferenceLine x={50} stroke="var(--border)" />
        <ReferenceLine y={50} stroke="var(--border)" />
        {/* Colored Quadrants */}
        <ReferenceArea
          x1={1}
          x2={50}
          y1={50}
          y2={100}
          fill="hsl(330, 50%, 85%)"
          fillOpacity={0.35}
        />
        {/* High arousal, unpleasant - Light purple/pink */}
        <ReferenceArea
          x1={50}
          x2={100}
          y1={50}
          y2={100}
          fill="hsl(45, 100%, 90%)"
          fillOpacity={0.35}
        />
        {/* High arousal, pleasant - Light yellow/cream */}
        <ReferenceArea
          x1={1}
          x2={50}
          y1={1}
          y2={50}
          fill="hsl(210, 80%, 90%)"
          fillOpacity={0.35}
        />
        {/* Low arousal, unpleasant - Light blue */}
        <ReferenceArea
          x1={50}
          x2={100}
          y1={1}
          y2={50}
          fill="hsl(120, 60%, 90%)"
          fillOpacity={0.35}
        />
        {/* Low arousal, pleasant - Light green */}
        <Scatter
          name="Predefined Emotions"
          data={emotions.slice(0, -1)}
          fill="hsl(180, 70%, 50%)"
        >
          <LabelList
            dataKey="emotion"
            position="top"
            className="fill-foreground text-xs font-medium"
          />
        </Scatter>
        <Scatter
          name="Predicted Emotion"
          data={[emotions[emotions.length - 1]]}
          fill="hsl(276, 70%, 60%)"
          shape={EmotionScatterChartPredictedDot}
        />
        <Legend
          verticalAlign="top"
          align="center"
          margin={{ bottom: 20 }}
          content={EmotionScatterChartLegendContent}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length > 0) {
              const data = payload[0].payload
              const name = data.emotion
              return (
                <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
                  <p className="font-medium text-primary">{name}</p>
                  <p className="text-sm text-muted-foreground">
                    Valence: {data.x.toFixed(1)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Arousal: {data.y.toFixed(1)}
                  </p>
                </div>
              )
            }
            return null
          }}
        />
      </ScatterChart>
    </ChartContainer>
  )
}

export default EmotionScatterChart
